import { forwardRef, type ReactNode } from "react";

import { AgentsIcon, Box, Row } from "@hightouch/htui";

import { useUser } from "src/contexts/user-context";
import { getInitials } from "src/utils/user";
import {
  type CommentThreadMarker,
  PIN_SHAPE,
  PIN_SIZE,
  type PinDragHandlers,
  UNREAD_PIN,
} from "src/components/comments/pin";

// Shared by hover, focus, and the rail pointing at this pin — the last can't be
// a CSS pseudo, since the cursor is over a different element.
const PIN_EMPHASIS = {
  transform: `translate(0, -${PIN_SIZE}px) scale(1.12)`,
  bg: "teal.200",
};

/**
 * Forwards a ref to the pin element: on the canvas the open card is placed
 * from its measured position, and revealing an off-screen pin measures the
 * same element.
 *
 * Takes a marker rather than a pin: an unpinned thread is drawn with the same
 * teardrop, docked rather than placed, so the marker is all this needs.
 */
export const PinMarker = forwardRef<
  HTMLButtonElement,
  {
    pin: CommentThreadMarker;
    isSelected: boolean;
    /** Its row in the rail is hovered. */
    isHighlighted: boolean;
    /** Both omitted where pins are read-only and can't be moved. */
    isDragging?: boolean;
    dragHandlers?: PinDragHandlers;
    onSelect: () => void;
    onHover: (isHovered: boolean) => void;
  }
>(function PinMarker(
  {
    pin,
    isSelected,
    isHighlighted,
    isDragging = false,
    dragHandlers,
    onSelect,
    onHover,
  },
  ref,
) {
  // Unread keeps its own colour through hover and focus; otherwise the emphasis
  // tint would read as "no longer unread" the moment the cursor arrived.
  const emphasis = pin.isUnread
    ? { ...PIN_EMPHASIS, bg: UNREAD_PIN.hover }
    : PIN_EMPHASIS;

  return (
    <Box
      ref={ref}
      as="button"
      type="button"
      // Not "outside" for the dismiss-on-press-outside listener: a press here
      // is this pin's to answer.
      data-comment-pin
      flexShrink={0}
      // Anchored by its point, not its centre — the bottom-left corner sits on
      // the spot the comment refers to.
      transform={`translate(0, -${PIN_SIZE}px)`}
      {...PIN_SHAPE}
      overflow="hidden"
      // Selected gets the design's full focus ring; unselected keeps the plain
      // white hairline from PIN_SHAPE so it reads against any ad.
      boxShadow={isSelected ? "outline" : PIN_SHAPE.boxShadow}
      // Grows from its own point rather than its centre, so the corner stays on
      // the spot it marks — a pin that drifted on hover would point somewhere
      // slightly wrong, and moving the target under the cursor makes its edges
      // flicker. Scale and background only: `boxShadow` already carries the
      // selected state, and animating a shadow repaints every frame.
      transformOrigin="bottom left"
      {...(pin.isUnread && { bg: UNREAD_PIN.bg })}
      {...(isHighlighted && emphasis)}
      _hover={emphasis}
      _focusVisible={{ ...emphasis, boxShadow: "outline" }}
      // A pin is a button first, so `grab` on hover would advertise moving it
      // over opening it. `_active` covers the press, `isDragging` holds through
      // a drag whose press-state the pin can lose.
      cursor={isDragging ? "grabbing" : "pointer"}
      {...(dragHandlers && { _active: { cursor: "grabbing" } })}
      transitionProperty="transform, background-color"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      // Chrome's own image drag, from the ad behind the pin, would cancel the
      // pointer stream mid-gesture.
      draggable={false}
      {...dragHandlers}
      onClick={onSelect}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      aria-label={
        pin.isUnread
          ? `Unread comment from ${pin.authorName}`
          : `Comment from ${pin.authorName}`
      }
    >
      <PinAvatar
        name={pin.authorName}
        isAgent={pin.isAgentThread}
        isUnread={pin.isUnread}
      />
    </Box>
  );
});

/**
 * The filled interior of a pin: initials on a solid ground, or the agents icon
 * where the thread is a review. Initials would spell a name nobody chose, and
 * the icon is what marks an agent everywhere else — see `ReviewAgentAvatar`.
 *
 * Drawn rather than composed from htui `Avatar` because the pin is a teardrop —
 * `Avatar` renders its own circular ground, which leaves the point unfilled
 * whatever shape the parent clips to. The ground comes from `PIN_SHAPE`, so the
 * colour reaches every corner.
 */
export const PinAvatar = ({
  name,
  isAgent = false,
  isUnread = false,
}: {
  name: string;
  isAgent?: boolean;
  isUnread?: boolean;
}) => (
  <PinFace isUnread={isUnread}>
    {isAgent ? (
      // Sized by font size, as AgentsIcon renders at 1em; "md" is the size
      // ReviewAgentAvatar pairs with the same 24px box.
      <Box as={AgentsIcon} fontSize="md" />
    ) : (
      getInitials(name)
    )}
  </PinFace>
);

/**
 * A new comment's pin, before it's posted: the author's own avatar, so it reads
 * as their comment before it exists. Ringed as an open pin is.
 */
export const DraftPin = () => {
  const { user } = useUser();
  return (
    <Box
      width={`${PIN_SIZE}px`}
      height={`${PIN_SIZE}px`}
      {...PIN_SHAPE}
      overflow="hidden"
      boxShadow="outline"
    >
      <PinAvatar name={user?.name ?? user?.email ?? ""} />
    </Box>
  );
};

/** A count of threads, drawn like a pin's initials so the two read the same side by side. */
export const PinCount = ({
  count,
  isUnread = false,
}: {
  count: number;
  isUnread?: boolean;
}) => <PinFace isUnread={isUnread}>{count}</PinFace>;

/** The text a pin shows on its ground. Private, so a pin shows only initials, the agents icon, or a count. */
const PinFace = ({
  isUnread = false,
  children,
}: {
  isUnread?: boolean;
  children: ReactNode;
}) => (
  <Row
    height={`${PIN_SIZE}px`}
    minWidth={`${PIN_SIZE}px`}
    align="center"
    justify="center"
    // Mirrors htui Avatar at size="md" — the same 24px box, so the initials
    // read at the same weight as avatars elsewhere in the thread.
    fontSize="10px"
    lineHeight={6}
    // Teal initials would fail contrast on the unread fill.
    color={isUnread ? "white" : "teal.700"}
    userSelect="none"
  >
    {children}
  </Row>
);
